import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Header, Footer, Stepper, PageCard, AudioRecorder } from "./index";
import { InterviewLanguage, uploadAudio } from "../utils/uploadAudio";
import { toast } from "sonner";
import BrowserLocalRecordingRecoveryPrompt from "./BrowserLocalRecordingRecoveryPrompt";
import BrowserLocalRecordingPlayback from "./BrowserLocalRecordingPlayback";
import { useBrowserLocalRecordingRecoveryPrompt } from "../hooks/useBrowserLocalRecordingRecoveryPrompt";
import { defaultBrowserLocalRecordingRecovery } from "../utils/browserLocalRecordingRecovery";

const RecordingPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [continuationSeedBlob, setContinuationSeedBlob] = useState<Blob | null>(
    null,
  );
  const [recoveredPlaybackBlob, setRecoveredPlaybackBlob] =
    useState<Blob | null>(null);
  const [continuationSeedDurationMs, setContinuationSeedDurationMs] =
    useState<number | undefined>(undefined);
  const [isRecordingInProgress, setIsRecordingInProgress] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [interviewLanguage, setInterviewLanguage] =
    useState<InterviewLanguage>("nl");
  const location = useLocation();
  const caseName = location.state?.caseName as string;
  const replaceExisting = Boolean(location.state?.replaceExisting);
  const [headerMessage, setHeaderMessage] = useState(
    replaceExisting
      ? "Maak een nieuwe opname. De vorige opname is vervangen."
      : "Start hier je gesprekshulp bij aanvang van het gesprek.",
  );
  // Once a recording has started (including an auto-started continuation), the
  // recording language is locked: we cannot change the language of an ongoing
  // recording, so the selector only writes the language until recording begins.
  const isLanguageSelectionDisabled =
    isUploading || isRecordingInProgress || Boolean(continuationSeedBlob);
  const {
    recoverableRecording,
    isInspectingRecovery,
    recoveryWarning,
    restoreRecoveredRecording,
    discardRecoveredRecording,
  } = useBrowserLocalRecordingRecoveryPrompt({
    conversationId,
    recordingType: "conversation",
    unavailableMessage:
      "Vorige opname kon niet worden hersteld. Je kunt een nieuwe opname starten.",
    inspectErrorMessage: "Could not inspect browser-local recording recovery:",
  });

  //get the model from localStorage
  // useEffect(() => {
  //   const model = localStorage.getItem('selectedModel');
  //   if (model) {
  //     setSelectedModel(model as ModelType);
  //   }
  // }, []);

  // Upload audio and navigate immediately
  const handleRecordingComplete = async (
    blob: Blob,
    lengthOfRecording: number,
  ) => {
    if (!conversationId) return;

    setAudioBlob(blob);
    setIsUploading(true);

    try {
      const workflowId = await uploadAudio({
        blob,
        conversationId,
        recordingType: "conversation",
        interviewLanguage,
      });
      await defaultBrowserLocalRecordingRecovery.discard(
        conversationId,
        "conversation",
      );
      setContinuationSeedBlob(null);
      setRecoveredPlaybackBlob(null);

      // uploadAudio already throws when the API returns no workflow_id.
      //sleep for 1 second, to make sure DBOS has saved the workflow_id
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Navigate immediately to TaskPage with workflow_id
      navigate(`/case/${conversationId}`, {
        state: { interviewWorkflowId: workflowId, lengthOfRecording },
      });
    } catch (error) {
      console.error("Error uploading audio:", error);
      setIsUploading(false);
      toast.error("Fout bij uploaden audio", {
        description:
          error instanceof Error ? error.message : "Onbekende fout opgetreden",
        duration: 8000,
      });
    }
  };

  // Handle recording reset
  const handleRecordingReset = () => {
    setAudioBlob(null);
    setContinuationSeedBlob(null);
    setRecoveredPlaybackBlob(null);
    setIsRecordingInProgress(false);
  };

  const handleFreshRecordingStart = async () => {
    // When continuing a recovered take, keep the recovered session in storage;
    // only a genuinely fresh recording should clear earlier recovery slots.
    setIsRecordingInProgress(true);

    if (!conversationId || continuationSeedBlob) return;

    await defaultBrowserLocalRecordingRecovery.prepareForFreshRecording(
      conversationId,
      "conversation",
    );
  };

  const handleRestoreRecoveredRecording = () => {
    const restoredRecording = restoreRecoveredRecording();
    if (!restoredRecording) return;

    setAudioBlob(restoredRecording.blob);
    setRecoveredPlaybackBlob(restoredRecording.blob);
    if (restoredRecording.metadata.interviewLanguage) {
      setInterviewLanguage(restoredRecording.metadata.interviewLanguage);
    }
    setHeaderMessage("Herstelde opname klaar om te verwerken.");
  };

  const handleContinueRecoveredRecording = async () => {
    const restoredRecording = restoreRecoveredRecording();
    if (!restoredRecording || !conversationId) return;

    setContinuationSeedBlob(restoredRecording.blob);
    setContinuationSeedDurationMs(restoredRecording.metadata.durationMs);
    setRecoveredPlaybackBlob(restoredRecording.blob);
    setAudioBlob(null);
    if (restoredRecording.metadata.interviewLanguage) {
      setInterviewLanguage(restoredRecording.metadata.interviewLanguage);
    }
    // Keep the recovered session in storage: the continuation is recorded as a
    // new session and the two are joined when the recording finishes.
    setHeaderMessage("Herstelde opname wordt aangevuld met een vervolgopname.");
  };

  const handleDiscardRecoveredRecording = async () => {
    await discardRecoveredRecording();
    setAudioBlob(null);
    setContinuationSeedBlob(null);
    setRecoveredPlaybackBlob(null);
  };

  if (!conversationId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Ongeldige conversatie ID
          </h1>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-brand-blauw text-brand-beige rounded-lg hover:bg-brand-blauw/90"
          >
            Terug naar home
          </button>
        </div>
      </div>
    );
  }

  const isRecovering = Boolean(recoverableRecording);
  // Once a take exists, the page is no longer an invitation to begin: the
  // recorder below shows the live state instead.
  // Until the local store has been read we do not know which screen applies,
  // so nothing that presumes an answer may render yet.
  const isConversationStarted =
    isRecovering || isRecordingInProgress || isInspectingRecovery;

  return (
    <div className="min-h-screen bg-brand-primary flex flex-col">
      <Header />

      <PageCard>
        <Stepper step={1} />

        {/* Recovering takes the full width: squeezed into the column beside the
            illustration, the three options wrap onto several lines each. */}
        <div
          className={
            isRecovering
              ? 'space-y-6'
              : 'flex flex-col lg:flex-row gap-12 lg:gap-16'
          }
        >
          {/* Left: Text & Controls */}
          <section className="flex-1 space-y-6">
            <div className={isRecovering ? 'space-y-6' : 'min-h-[320px] space-y-6'}>
              {isRecovering ? (
                <span className="badge-context">Eerder gestart gesprek</span>
              ) : (
                // While recording, the recorder shows its own "Actieve opname"
                // badge; a second one inviting a start would contradict it.
                !isRecordingInProgress && (
                  <span className="badge-context">Start van het gesprek met cliënt</span>
                )
              )}

              <div>
                <h1 className="text-3xl font-semibold text-brand-red">
                  {caseName ? caseName : 'Gesprek met ID: ' + conversationId}
                </h1>
                {/* The start prompt would contradict a recording that is
                    already running, or the pause notice below. */}
                {!isConversationStarted && (
                  <p className="text-xl text-brand-blauw leading-relaxed">
                    {headerMessage}
                  </p>
                )}
              </div>

              {recoveryWarning && (
                <div
                  className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                  role="status"
                >
                  {recoveryWarning}
                </div>
              )}

              {isInspectingRecovery ? (
                <p className="text-base text-brand-blauw" role="status">
                  Controleren op een eerdere opname…
                </p>
              ) : recoverableRecording ? (
                <BrowserLocalRecordingRecoveryPrompt
                  title="De gespreksopname is gepauzeerd."
                  description="Dit gesprek is eerder gestart en actief gepauzeerd of door een probleem met internet afgebroken."
                  durationMs={recoverableRecording.metadata.durationMs}
                  onRestore={handleRestoreRecoveredRecording}
                  onContinue={handleContinueRecoveredRecording}
                  onDiscard={handleDiscardRecoveredRecording}
                />
              ) : (
                <>
                  <BrowserLocalRecordingPlayback
                    blob={recoveredPlaybackBlob}
                    label="Herstelde opname"
                  />
                  <AudioRecorder
                    conversationId={conversationId}
                    onRecordingComplete={handleRecordingComplete}
                    onReset={handleRecordingReset}
                    onSubmit={() => {
                      if (audioBlob) {
                        handleRecordingComplete(audioBlob, 0);
                      }
                    }}
                    audioBlob={audioBlob}
                    isSubmitting={isUploading}
                    setHeaderMessage={setHeaderMessage}
                    recordingType="conversation"
                    interviewLanguage={interviewLanguage}
                    onInterviewLanguageChange={setInterviewLanguage}
                    isLanguageSelectionDisabled={isLanguageSelectionDisabled}
                    onRecordingStart={handleFreshRecordingStart}
                    leadingChunkBlob={continuationSeedBlob}
                    leadingDurationMs={continuationSeedDurationMs}
                    autoStart={Boolean(continuationSeedBlob)}
                  />
                </>
              )}
            </div>
          </section>

          {/* Right: Illustration */}
          <figure
            className={
              isRecovering
                ? 'hidden'
                : 'hidden lg:flex flex-1 items-center justify-end'
            }
            aria-hidden="true"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/man-achter-bureau-met-client.svg`}
              alt=""
              className="max-h-[360px] w-auto"
              loading="lazy"
            />
          </figure>
        </div>
      </PageCard>

      <Footer />
    </div>
  );
};

export default RecordingPage;
